import axios from "axios";
import { inject, injectable } from "inversify";
import { IProjectRepository } from "@domain/repositories";
import type {
  CreateProject,
  EffectiveSoftwareVersionContextConfiguration,
  EffectiveStorageContextConfiguration,
  PathTypeSchema,
  PathVisibilitySchema,
  SoftwareEnumSchema,
  SoftwareVersionContextConfiguration,
  StorageContextConfiguration,
} from "@domain/schemas/BI";
import type { MyProject, ProjectDetail, ProjectMoveRequest, ProjectRoleName } from "@domain/schemas/workspace";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

// Implementation of the IProjectRepository interface
@injectable()
export class ProjectRepository implements IProjectRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getMyProjectsAsync(): Promise<MyProject[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projects}`;
    return this._requestHandlerService.handleRequestAsync<MyProject[]>(() => axios.get<MyProject[]>(url));
  }

  public async getProjectAsync(projectId: string): Promise<ProjectDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectById}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<ProjectDetail>(() => axios.get<ProjectDetail>(url));
  }

  public async getEffectiveStorageConfigurationAsync(projectId: string): Promise<EffectiveStorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationEffective}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<EffectiveStorageContextConfiguration>(() => axios.get<EffectiveStorageContextConfiguration>(url));
  }

  public async getEffectiveSoftwareVersionConfigurationAsync(projectId: string): Promise<EffectiveSoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfigurationEffective}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<EffectiveSoftwareVersionContextConfiguration>(() => axios.get<EffectiveSoftwareVersionContextConfiguration>(url));
  }

  public async getSoftwareVersionConfigurationAsync(projectId: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfiguration}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.get<SoftwareVersionContextConfiguration>(url));
  }

  public async addSoftwareVersionAsync(projectId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfigurationVersions}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.post<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async removeSoftwareVersionAsync(projectId: string, versionId: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfigurationVersionById}`.replace("{projectId}", () => projectId).replace("{versionId}", () => versionId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }

  public async setSoftwareVersionDefaultAsync(projectId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfigurationDefault}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.put<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async clearSoftwareVersionDefaultAsync(projectId: string, software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectSoftwareVersionConfigurationDefaultForSoftware}`.replace("{projectId}", () => projectId).replace("{software}", () => software);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }

  public async getStorageConfigurationAsync(projectId: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfiguration}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.get<StorageContextConfiguration>(url));
  }

  public async addStoragePathAsync(projectId: string, pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationPaths}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.post<StorageContextConfiguration>(url, { pathType, path, visibility }));
  }

  public async removeStoragePathAsync(projectId: string, pathId: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationPathById}`.replace("{projectId}", () => projectId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.delete<StorageContextConfiguration>(url));
  }

  public async setStoragePathVisibilityAsync(projectId: string, pathId: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationPathVisibility}`.replace("{projectId}", () => projectId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { visibility }));
  }

  public async setStoragePathValueAsync(projectId: string, pathId: string, path: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationPathValue}`.replace("{projectId}", () => projectId).replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { path }));
  }

  public async setStorageResultsPathAsync(projectId: string, resultsPath: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectStorageConfigurationResultsPath}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { resultsPath }));
  }

  public async createProjectAsync(teamId: string, request: CreateProject): Promise<ProjectDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamProjectInventory}`.replace("{teamId}", () => teamId);
    return this._requestHandlerService.handleRequestAsync<ProjectDetail>(() => axios.post<ProjectDetail>(url, request));
  }

  public async renameProjectAsync(projectId: string, newName: string): Promise<ProjectDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectName}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<ProjectDetail>(() => axios.put<ProjectDetail>(url, { newName }));
  }

  public async deleteProjectAsync(teamId: string, projectId: string): Promise<void> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamProjectById}`.replace("{teamId}", () => teamId).replace("{projectId}", () => projectId);
    await this._requestHandlerService.handleRequestAsync<boolean>(() => axios.delete<boolean>(url));
  }

  public async assignProjectRoleAsync(projectId: string, targetUserId: string, role: ProjectRoleName): Promise<ProjectDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectMembers}`.replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<ProjectDetail>(() => axios.put<ProjectDetail>(url, { targetUserId, role }));
  }

  public async removeProjectMemberAsync(projectId: string, userId: string): Promise<ProjectDetail> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.projectMemberById}`.replace("{projectId}", () => projectId).replace("{userId}", () => userId);
    return this._requestHandlerService.handleRequestAsync<ProjectDetail>(() => axios.delete<ProjectDetail>(url));
  }

  public async proposeProjectMoveAsync(sourceTeamId: string, projectId: string, targetTeamId: string): Promise<ProjectMoveRequest> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamProjectMoveRequests}`.replace("{teamId}", () => sourceTeamId).replace("{projectId}", () => projectId);
    return this._requestHandlerService.handleRequestAsync<ProjectMoveRequest>(() => axios.post<ProjectMoveRequest>(url, { targetTeamId }));
  }

  public async acceptProjectMoveAsync(targetTeamId: string, requestId: string, newManagerUserId?: string): Promise<ProjectMoveRequest> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMoveRequestAccept}`.replace("{teamId}", () => targetTeamId).replace("{requestId}", () => requestId);
    return this._requestHandlerService.handleRequestAsync<ProjectMoveRequest>(() => axios.post<ProjectMoveRequest>(url, { newManagerUserId: newManagerUserId ?? null }));
  }

  public async rejectProjectMoveAsync(targetTeamId: string, requestId: string): Promise<ProjectMoveRequest> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMoveRequestReject}`.replace("{teamId}", () => targetTeamId).replace("{requestId}", () => requestId);
    return this._requestHandlerService.handleRequestAsync<ProjectMoveRequest>(() => axios.post<ProjectMoveRequest>(url));
  }

  public async withdrawProjectMoveAsync(sourceTeamId: string, requestId: string): Promise<ProjectMoveRequest> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMoveRequestWithdraw}`.replace("{teamId}", () => sourceTeamId).replace("{requestId}", () => requestId);
    return this._requestHandlerService.handleRequestAsync<ProjectMoveRequest>(() => axios.post<ProjectMoveRequest>(url));
  }

  public async listPendingProjectMoveRequestsAsync(targetTeamId: string): Promise<ProjectMoveRequest[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.teamMoveRequestsPending}`.replace("{teamId}", () => targetTeamId);
    return this._requestHandlerService.handleRequestAsync<ProjectMoveRequest[]>(() => axios.get<ProjectMoveRequest[]>(url));
  }
}
